import { __ } from '../translator';

const STORAGE_KEYS = ['registerFinalData', 'whatsappFinalData', 'calendarFinalData'];

export function getLatestCalendarFinalData(serverData = null) {
    let data = serverData && typeof serverData === 'object' ? { ...serverData } : null;
    let latestTime = data?.updatedAt || 0;

    if (data) {
        data.updatedAt = data.updatedAt || Date.now();
        return data;
    }

    STORAGE_KEYS.forEach((key) => {
        const val = localStorage.getItem(key);
        if (!val) {
            return;
        }
        try {
            const parsed = JSON.parse(val);
            if (parsed.updatedAt && parsed.updatedAt > latestTime) {
                data = parsed;
                latestTime = parsed.updatedAt;
            } else if (!parsed.updatedAt && !data) {
                data = parsed;
            }
        } catch (e) {
            console.error('Error parsing', key, e);
        }
    });

    return data;
}

// Per-theme renderers. Missing methods fall through to DEFAULT_RENDERER.
const THEME_RENDERERS = {
    yo_me_animo: {
        noData(config) {
            const msg = config.customSuccessMessage?.trim()
                ? config.customSuccessMessage
                : (config.calendarCommonSubmissionText || __('landing.calendar_common_submission_text'));
            return `<div class="yomeanimo-next-steps-desc">${msg}</div>`;
        },
        notQualified(_data, _config) {
            return '';
        },
        whatsapp(data, config) {
            const advisorTitle = config.yoMeAnimoAdvisorTitle || '';
            const talkNow = config.yoMeAnimoTalkNow || '';
            return `
                <div class="yomeanimo-appointment yomeanimo-whatsapp-only">
                    ${data.guide.image ? `<img src="${data.guide.image}" alt="${data.guide.name}" class="yomeanimo-advisor-image">` : '<div class="yomeanimo-advisor-placeholder"></div>'}
                    <p class="yomeanimo-advisor-name">${data.guide.name || ''}</p>
                    <p class="yomeanimo-advisor-title">${advisorTitle}</p>
                    <a href="${data.whatsapp_link}" target="_blank" class="yomeanimo-whatsapp-btn"><i class="fab fa-whatsapp"></i><span class="yomeanimo-whatsapp-btn-text">${talkNow}</span></a>
                </div>
            `;
        },
        appointment(data, config) {
            const advisorTitle = config.yoMeAnimoAdvisorTitle || '';
            const talkNow = config.yoMeAnimoTalkNow || '';
            const minutesLabel = config.minutesLabel || '';
            const submissionText = resolveSubmissionText(data, config);
            return `
                <div class="yomeanimo-appointment">
                    ${(data.user?.image || data.guide?.image) ? `<img src="${data.user?.image || data.guide?.image}" alt="${data.user?.name || data.guide?.name || ''}" class="yomeanimo-advisor-image">` : '<div class="yomeanimo-advisor-placeholder"></div>'}
                    <p class="yomeanimo-advisor-name">${data.user?.name || data.guide?.name || ''}</p>
                    <p class="yomeanimo-advisor-title">${advisorTitle}</p>
                    <h4 class="yomeanimo-appointment-type">${data.appointment_type || ''}</h4>
                    <div class="yomeanimo-appointment-info">
                        <p><i class="fal fa-clock"></i> ${data.duration || ''} ${minutesLabel}</p>
                        <p><i class="fal fa-calendar"></i> ${data.datetime || ''}</p>
                        <p><i class="fal fa-globe"></i> ${data.timezone || ''}</p>
                    </div>
                    ${data.whatsapp_link ? `<a href="${data.whatsapp_link}" target="_blank" class="yomeanimo-whatsapp-btn"><i class="fab fa-whatsapp"></i><span class="yomeanimo-whatsapp-btn-text">${talkNow}</span></a>` : ''}
                </div>
            `;
        },
    },

    marketing_website: {
        notQualified(data, config) {
            const anchor = '<span class="success-message-anchor" aria-hidden="true"></span>';
            const msg = resolveNoCriteriaMessage(data, config);
            return `<div class="marketing-simple-msg">${anchor}<div class="marketing-simple-msg__body">${msg}</div></div>`;
        },
    },
};

// Fallback used when a theme has no override for a scenario.
const DEFAULT_RENDERER = {
    noData(_config) {
        return '';
    },
    notQualified(data, config) {
        const anchor = '<span class="success-message-anchor" aria-hidden="true"></span>';
        const searchImage = config.searchDocumentImage || '';
        const msg = resolveNoCriteriaMessage(data, config);
        return ''
            + `<div class="image thank-you"><img src="${searchImage}" alt="Thank you" /></div>`
            + `<div class="message">${anchor}<strong>${msg}</strong></div>`;
    },
    whatsapp(data, config) {
        const msg = config.customSuccessMessage?.trim() ? config.customSuccessMessage : (data.description || '');
        return `
            <div class="whatsapp-card p-5 mb-3 text-center">
                ${data.guide.image ? `<img src="${data.guide.image}" alt="${data.guide.name}" class="consultant-image mb-2">` : ''}
                <p class="consultant-name">${data.guide.name || ''}</p>
                <div class="consultant-description"><span class="success-message-anchor" aria-hidden="true"></span>${msg}</div>
                <a href="${data.whatsapp_link}" target="_blank" class="btn btn-success whatsapp-btn mt-3">
                    <i class="fab fa-whatsapp"></i> Chat on WhatsApp
                </a>
            </div>
        `;
    },
    appointment(data, config) {
        const minutesLabel = config.minutesLabel || '';
        const searchImage = config.searchDocumentImage || '';
        const calendarMsg = config.customSuccessMessage?.trim()
            ? config.customSuccessMessage
            : resolveSubmissionText(data, config);
        return `
            <div class="image thank-you">
                <img src="${searchImage}" alt="Thank you" />
            </div>
            <div class="message"><span class="success-message-anchor" aria-hidden="true"></span>${calendarMsg}</div>
            <div class="appointment-card card p-5 mb-3 text-center">
                ${data.user?.image ? `<img src="${data.user.image}" alt="${data.user.name}" class="consultant-image">` : ''}
                <p class="consultant-name">${data.user?.name || ''}</p>
                <h4 class="appointment-type">${data.appointment_type || ''}</h4>
                <div class="appointment-info text-left mt-3">
                    <p><i class="fal fa-clock"></i> ${data.duration || ''} ${minutesLabel}</p>
                    <p><i class="fal fa-calendar"></i> ${data.datetime || ''}</p>
                    <p><i class="fal fa-globe"></i> ${data.timezone || ''}</p>
                </div>
                ${data.whatsapp_link ? `
                    <a href="${data.whatsapp_link}" target="_blank" class="btn btn-success whatsapp-btn">
                        <i class="fab fa-whatsapp"></i> Chat on WhatsApp
                    </a>
                ` : ''}
            </div>
        `;
    },
};

function getRenderer(embedTheme, method) {
    return THEME_RENDERERS[embedTheme]?.[method] ?? DEFAULT_RENDERER[method];
}

function resolveNoCriteriaMessage(data, config) {
    const custom = config.customSuccessMessage?.trim();
    return custom ? custom : (data?.text || '');
}

function resolveSubmissionText(data, config) {
    const appointmentType = data.appointment_type?.toLowerCase().trim() || '';
    const submissionMap = config.submissionMap || [];
    const fallback = config.calendarCommonSubmissionText || __('landing.calendar_common_submission_text');
    for (const { keys, text } of submissionMap) {
        if (keys.includes(appointmentType)) {
            return text;
        }
    }
    return fallback;
}

export function buildCalendarFinalDetailsHtml(data, config = {}) {
    const embedTheme = config.embedTheme || 'default';

    if (!data) {
        return getRenderer(embedTheme, 'noData')(config);
    }

    if (data.whatsapp_link && data.guide) {
        return getRenderer(embedTheme, 'whatsapp')(data, config);
    }

    if (data.appointment_type) {
        return getRenderer(embedTheme, 'appointment')(data, config);
    }

    return getRenderer(embedTheme, 'notQualified')(data, config);
}

window.LandingCalendarFinalDetails = {
    getLatestCalendarFinalData,
    buildCalendarFinalDetailsHtml,
};
